import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { Button } from "../components/ui/button";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { loadSubscription } = useAuth();
  const [state, setState] = useState("checking");
  const attempts = useRef(0);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("error"); return; }
    let timer;
    const poll = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          await loadSubscription();
          setState("success");
          return;
        }
        if (["expired", "failed"].includes(data.payment_status) || attempts.current > 15) {
          setState("error");
          return;
        }
      } catch {
        if (attempts.current > 15) { setState("error"); return; }
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [params, loadSubscription]);

  return (
    <div className="min-h-screen grain flex items-center justify-center px-6">
      <div className="bg-[#141419] border border-white/10 rounded-2xl p-10 max-w-md text-center fade-up">
        {state === "checking" && (
          <>
            <Loader2 className="w-12 h-12 text-amber-400 animate-spin mx-auto mb-4" />
            <h1 className="font-display text-2xl font-bold mb-2">Confirming your subscription…</h1>
            <p className="text-zinc-500">Hang tight, this only takes a moment.</p>
          </>
        )}
        {state === "success" && (
          <>
            <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto mb-4" />
            <h1 className="font-display text-2xl font-bold mb-2">You're all set!</h1>
            <p className="text-zinc-400 mb-6">Your subscription is active and credits are allocated. Time to clip.</p>
            <Button onClick={() => navigate("/workspace")} data-testid="go-workspace-btn" className="bg-amber-500 hover:bg-amber-400 text-zinc-950 font-bold">
              Go to AI Clipping
            </Button>
          </>
        )}
        {state === "error" && (
          <>
            <XCircle className="w-14 h-14 text-red-400 mx-auto mb-4" />
            <h1 className="font-display text-2xl font-bold mb-2">Payment not confirmed</h1>
            <p className="text-zinc-400 mb-6">We couldn't confirm your payment. If you were charged, it may take a minute — otherwise try again.</p>
            <Button onClick={() => navigate("/pricing")} className="bg-amber-500 hover:bg-amber-400 text-zinc-950 font-bold">
              Back to Pricing
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
